import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import UnstyledImage from "./image";
import { useWindowSize } from "../utils/use-window-size.js";
import HeroCarousel from "./hero-carousel";

const HeroWrapper = styled.div`
  width: 100%;
  position: relative;
  color: var(--color-white);
  font-family: var(--font-paragraphs);

  h1 {
    font-weight: 300;
    font-size: 40px;
    margin-bottom: 10px;
    text-shadow: 0px 1px 6px rgba(0, 0, 0, 0.16);
    font-family: var(--font-headings);
  }
  p {
    text-transform: uppercase;
    font-size: 40px;
    font-weight: 600;
    margin: 0 0 1rem 0;
    text-shadow: 0px 1px 6px rgba(0, 0, 0, 0.26);
    font-family: var(--font-paragraphs);
  }
`;

const BigHero = styled(HeroWrapper)`
  height: 609px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 430px;
  }
`;
const FrontpageHero = styled(HeroWrapper)`
  height: 609px;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: 90vh;
  }
`;
const SmHero = styled(HeroWrapper)`
  height: 368px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 195px;
  }
`;
const HeroImage = styled(UnstyledImage)`
  height: 100%;
  width: 100%;
  object-fit: cover;
  position: absolute;
  z-index: 0;
`;

const HeroVideo = styled.video`
  height: 100%;
  width: 100%;
  object-fit: cover;
  position: absolute;
  z-index: 0;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 100px 0px 0 70px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 20px 40px;
    justify-content: center;
    height: 70%;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: 100%;
    padding: 20px;
  }
  a {
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      width: 100%;
      margin: 5px 0;
      max-width: 500px;
    }
    h1 {
      font-weight: 300;
      font-size: 3.3rem;
      margin-bottom: 10px;
      text-shadow: 0px 1px 6px rgba(0, 0, 0, 0.16);
      @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
        font-size: 2.2rem;
      }
    }
    h2 {
      text-transform: uppercase;
      font-size: 3.3rem;
      font-weight: 600;
      margin: 0 0 1rem 0;
      text-shadow: 0px 1px 6px rgba(0, 0, 0, 0.26);
      @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
        font-size: 2.2rem;
      }
    }
  }
`;

const FrontPageHeroContent = styled(HeroContent)`
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: 80vh;
  }
`;
const SideBySideContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  color: #000;
  background-color: #ededed;
  justify-content: center;
  padding: 3rem;
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor || `var(--color-secondary)`};
  color: ${({ textColor }) => textColor || `var(--color-black)`};
  p {
    text-shadow: none;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding: 1.5rem;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
  }
`;
const SideBySideHeroVideo = styled.video`
  height: 100%;
  width: 50%;
  object-fit: cover;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
  }
`;
const SideBySideHeroImage = styled(UnstyledImage)`
  height: 100%;
  width: 50%;
  object-fit: cover;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
  }
`;
const SideBySideHero = styled.div`
  display: flex;
  width: 100%;
  font-family: var(--font-paragraphs);
  gap: 1rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    flex-direction: column;
  }
`;
const SideBySideCarouselWrapper = styled.div`
  width: 50%;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
  }
`;
const CarouselChildren = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-60%);
  min-width: 100%;
  max-height: 100%;
  display: flex;
  flex-flow: column wrap;
  align-items: center;
  justify-content: center;
`;
const CarouselChildrenInnerWrapper = styled.div`
  max-width: 75%;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 65%;
  }
`;
export default function Hero({
  mediaUrl,
  mediaAlt,
  media,
  mobileMedia,
  variant,
  children,
  mediaItems,
  mediaItemsMobile,
  backgroundColorHex,
  textColorHex,
  className,
}) {
  const multipleMedia = media?.mimeType && mobileMedia?.mimeType;
  const [heroMedia, setMedia] = React.useState(multipleMedia ? null : media);
  const windowSize = useWindowSize();

  React.useEffect(() => {
    if (windowSize.width && multipleMedia) {
      if (windowSize.width <= 600) {
        setMedia(mobileMedia);
      } else {
        setMedia(media);
      }
    }
  }, [windowSize]);

  if (variant === "big") {
    return (
      <BigHero>
        {heroMedia?.mimeType?.match(/^video/) ? (
          <>
            <HeroVideo autoPlay loop muted playsInline>
              <source
                src={heroMedia.localFile.publicURL}
                type={heroMedia.mimeType}
                alt={heroMedia.mediaAlt}
              />
            </HeroVideo>
            <HeroContent>{children}</HeroContent>
          </>
        ) : mediaItems?.length > 0 || mediaItemsMobile?.length > 0 ? (
          <>
            <HeroCarousel
              mediaItems={mediaItems}
              mediaItemsMobile={mediaItemsMobile}
              variant={variant}
            ></HeroCarousel>
            <CarouselChildren>
              <CarouselChildrenInnerWrapper>
                {children}
              </CarouselChildrenInnerWrapper>
            </CarouselChildren>
          </>
        ) : (
          <>
            <HeroImage
              src={mediaUrl}
              alt={mediaAlt}
              image={heroMedia}
              loading="eager"
            />
            <HeroContent>{children}</HeroContent>
          </>
        )}
      </BigHero>
    );
  } else if (variant === "frontpage") {
    return (
      <FrontpageHero>
        {heroMedia?.mimeType?.match(/^video/) ? (
          <>
            <HeroVideo autoPlay loop muted playsInline>
              <source
                src={heroMedia.localFile.publicURL}
                type={heroMedia.mimeType}
                alt={heroMedia.mediaAlt}
              />
            </HeroVideo>
            <FrontPageHeroContent>{children}</FrontPageHeroContent>
          </>
        ) : mediaItems?.length > 0 || mediaItemsMobile?.length > 0 ? (
          <>
            <HeroCarousel
              mediaItems={mediaItems}
              mediaItemsMobile={mediaItemsMobile}
              variant={variant}
            ></HeroCarousel>
            <CarouselChildren>
              <CarouselChildrenInnerWrapper>
                {children}
              </CarouselChildrenInnerWrapper>
            </CarouselChildren>
          </>
        ) : (
          <>
            <HeroImage
              src={mediaUrl}
              alt={mediaAlt}
              image={heroMedia}
              loading="eager"
            />
            <FrontPageHeroContent>{children}</FrontPageHeroContent>
          </>
        )}
      </FrontpageHero>
    );
  } else if (variant === "small") {
    return (
      <SmHero>
        {heroMedia?.mimeType?.match(/^video/) ? (
          <>
            <HeroVideo autoPlay loop muted playsInline>
              <source
                src={heroMedia.localFile.publicURL}
                type={heroMedia.mimeType}
                alt={heroMedia.mediaAlt}
              />
            </HeroVideo>
          </>
        ) : mediaItems?.length > 0 || mediaItemsMobile?.length > 0 ? (
          <>
            <HeroCarousel
              mediaItems={mediaItems}
              mediaItemsMobile={mediaItemsMobile}
              variant={variant}
            ></HeroCarousel>
            <CarouselChildren>
              <CarouselChildrenInnerWrapper>
                {children}
              </CarouselChildrenInnerWrapper>
            </CarouselChildren>
          </>
        ) : (
          <>
            <HeroImage
              src={mediaUrl}
              alt={mediaAlt}
              image={heroMedia}
              loading="eager"
            />
          </>
        )}
      </SmHero>
    );
  } else if (variant === "side-by-side") {
    return (
      <SideBySideHero className={className}>
        {heroMedia?.mimeType?.match(/^video/) ? (
          <>
            <SideBySideHeroVideo autoPlay loop muted playsInline>
              <source
                src={heroMedia.localFile.publicURL}
                type={heroMedia.mimeType}
                alt={heroMedia.mediaAlt}
              />
            </SideBySideHeroVideo>
          </>
        ) : mediaItems?.length > 0 || mediaItemsMobile?.length > 0 ? (
          <SideBySideCarouselWrapper>
            <HeroCarousel
              mediaItems={mediaItems}
              mediaItemsMobile={mediaItemsMobile}
            />
          </SideBySideCarouselWrapper>
        ) : (
          <>
            <SideBySideHeroImage
              src={mediaUrl}
              alt={mediaAlt}
              image={heroMedia}
              loading="eager"
            />
          </>
        )}
        <SideBySideContent
          backgroundColor={backgroundColorHex}
          textColor={textColorHex}
        >
          {children}
        </SideBySideContent>
      </SideBySideHero>
    );
  }
}

Hero.propTypes = {
  mediaUrl: PropTypes.object,
  mediaAlt: PropTypes.string,
  media: PropTypes.object,
  mobileMedia: PropTypes.object,
  children: PropTypes.node,
  variant: PropTypes.oneOf(["big", "frontpage", "small", "side-by-side"]),
  mediaItems: PropTypes.arrayOf(PropTypes.object),
  mediaItemsMobile: PropTypes.arrayOf(PropTypes.object),
  backgroundColorHex: PropTypes.string,
  textColorHex: PropTypes.string,
  className: PropTypes.string,
};

Hero.defaultProps = {
  children: undefined,
  mediaUrl: undefined,
  mediaAlt: undefined,
  media: undefined,
  mobileMedia: undefined,
  variant: "big",
  mediaItems: undefined,
  mediaItemsMobile: undefined,
};
